// taskbox.js — the "current task" box: what is shown, the lock state, and the
// active task with its timer.

import { S, placeholderArt } from './state.js';
import { persistActive, readActive } from './local.js';

const currentTaskText = document.getElementById('currentTaskText');
const nextBtn = document.getElementById('nextTask');
const doneBtn = document.getElementById('markDone');
const lockStatus = document.getElementById('lockStatus');
const tagChip = document.getElementById('taskTag');
const bowlHint = document.getElementById('bowlHint');

// The empty bowl says what to do next; a drawn task needs no instructions.
function setHint(text) { if (bowlHint) { bowlHint.textContent = text || ''; bowlHint.hidden = !text; } }

// The tag rides in its own chip above the task, so the task text itself stays
// clean (it used to be prefixed with "[Tag] ").
function setTagChip(tag) {
  if (!tagChip) return;
  tagChip.textContent = tag || '';
  tagChip.hidden = !tag;
}
const releaseBtn = document.getElementById('releaseTask');
const durationEl = document.getElementById('taskDurationDisplay');

// Screen-reader announcements go through a visually hidden live region;
// the task box itself contains ASCII art that would be read out otherwise.
export function announce(msg) {
  const el = document.getElementById('taskAnnounce');
  if (el) { el.textContent = ''; setTimeout(() => { el.textContent = msg; }, 30); }
}

export function showTask(task) {
  setHint('');
  setTagChip(task.tag);
  currentTaskText.textContent = task.text;
  currentTaskText.classList.remove('placeholder');
  currentTaskText.removeAttribute('aria-hidden');
  announce(`Now working on: ${task.text}`);
}

export function showPlaceholder() {
  const n = S.tasks.length;
  setHint(n ? `${n} task${n === 1 ? '' : 's'} in the bowl — press Next! to draw one.`
            : 'The bowl is empty. Add a few tasks below, then press Next!');
  setTagChip('');
  currentTaskText.textContent = placeholderArt;
  currentTaskText.classList.add('placeholder');
  currentTaskText.setAttribute('aria-hidden', 'true'); // decorative fish art
}

// A short message in the task box, then back to the fish art.
export function flashMessage(text, ms) {
  setHint('');
  setTagChip('');
  currentTaskText.textContent = text;
  currentTaskText.classList.remove('placeholder');
  currentTaskText.removeAttribute('aria-hidden');
  announce(text);
  setTimeout(showPlaceholder, ms);
}

export function setDurationDisplay(text) {
  if (durationEl) durationEl.textContent = text;
}

export function updateLockState() {
  const locked = S.currentFishId !== null;
  // "Fish locked in" is informational by design: Next stays usable and skips.
  nextBtn.classList.toggle('is-muted', locked);
  doneBtn.disabled = false;
  doneBtn.classList.remove('is-muted');
  // Only one button is the loud one, and it is the one to press now: draw a
  // task when the bowl is idle, finish it once a fish is in.
  nextBtn.classList.toggle('is-leading', !locked);
  doneBtn.classList.toggle('is-leading', locked);
  if (lockStatus) lockStatus.style.display = locked ? 'inline' : 'none';
  if (releaseBtn) releaseBtn.style.display = locked ? 'inline-block' : 'none';
}

export function setActiveTask(task, startedAt) {
  S.currentFishId = task.id;
  S.currentFishStartTime = startedAt ? new Date(startedAt) : new Date();
  if (isNaN(S.currentFishStartTime)) S.currentFishStartTime = new Date();
  showTask(task);
  updateLockState();
  persistActive();
}

// Clears state only; callers decide what the task area shows next
// (the "Done" animation must not be interrupted).
export function clearActiveTask() {
  S.currentFishId = null;
  S.currentFishStartTime = null;
  persistActive();
}

// Restore the active task after a reload. This device's own record wins;
// the cloud record (from another device) is the fallback.
export function restoreActiveTask(cloudActive) {
  const local = readActive();
  for (const candidate of [local, cloudActive]) {
    const task = candidate?.id ? S.tasks.find(t => t.id === candidate.id) : null;
    if (task) { setActiveTask(task, candidate.startedAt); return; }
  }
  if (S.currentFishId) { clearActiveTask(); showPlaceholder(); }
}

// Used when the active task is removed from under us (deleted, or gone elsewhere).
export function dropActiveTask() {
  clearActiveTask();
  showPlaceholder();
  setDurationDisplay('');
  updateLockState();
}
